import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import { getBlogPosts } from '../lib/blog';
import { ComparisonTable } from '../components/ComparisonTable';
import { BlogCard } from '../components/BlogCard';
import HeroCarousel from '../components/HeroCarousel';
import { propFirmData } from '../data/propFirmDataHomepage';
import type { BlogPost } from '../types';

export function HomePage() {
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);

  useEffect(() => {
    getBlogPosts().then((posts) => {
      setBlogPosts(posts.slice(0, 3));
    });
  }, []);

  return (
    <>
      <Helmet>
        <title>PropViper – Top Prop Firm Reviews & Trader Insights</title>
        <meta name="description" content="Discover the best prop trading firms, honest reviews, and expert trading tips. Compare firms and get funded faster with PropViper." />
        <link rel="canonical" href="https://propviper.com/" />
      </Helmet>

      {/* HERO SECTION */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 overflow-hidden py-16 md:py-24">
        <video autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover opacity-20">
          <source src="/assets/hero-bg.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/40 to-black/70" />

        <div className="relative z-10 container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-10">
            <div className="text-center md:text-left md:w-1/2">
              <span className="text-sm font-medium text-yellow-400 uppercase tracking-widest">
                Prop Firm Comparison
              </span>
              <div className="bg-white/10 backdrop-blur-md p-6 sm:p-8 rounded-xl text-white shadow-lg mt-4">
                <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight text-yellow-400">
                  Get Funded by the Best <br /> Prop Trading Firms
                </h1>
                <p className="text-base sm:text-lg text-yellow-600 mt-4">
                  Compare evaluations, find exclusive discounts, and read honest reviews before taking your next prop challenge.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mt-6 justify-center md:justify-start">
                  <Link to="/compare" className="bg-yellow-400 text-black px-6 py-3 rounded-full font-semibold hover:bg-yellow-300 transition text-center">
                    Compare Firms
                  </Link>
                  <Link to="/reviews" className="bg-white text-yellow-700 px-6 py-3 rounded-full font-semibold hover:bg-yellow-100 transition text-center">
                    Read Reviews
                  </Link>
                </div>
                <p className="text-sm text-yellow-600 mt-6">Helping traders find the right prop firm</p>
              </div>
            </div>

            <div className="md:w-1/2 w-full flex flex-col items-center gap-4 mt-6 md:mt-0">
              <HeroCarousel />
            </div>
          </div>
        </div>
      </section>

      {/* COMPARISON TABLE SECTION */}
      <section className="py-20 bg-gray-950 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-yellow-400">Top Prop Trading Firms Compared</h2>
            <p className="text-yellow-300 text-lg">
              We’ve analyzed the best prop trading firms to help you make the right choice.
            </p>
          </div>

          <ComparisonTable firms={Object.values(propFirmData)} />

          <div className="mt-16 text-center">
            <Link
              to="/compare"
              className="inline-flex items-center justify-center bg-white/10 backdrop-blur-md px-6 py-3 rounded-full text-yellow-400 font-semibold shadow-md hover:bg-white/20 transition"
            >
              View Full Comparison
            </Link>
          </div>
        </div>
      </section>

      {/* BLOG SECTION */}
      {blogPosts.length > 0 && (
        <section className="py-20 pb-4 bg-black text-yellow-100">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-yellow-400">Latest Insights</h2>
              <p className="text-yellow-300">New trading strategies, prop firm breakdowns, and tips from funded traders.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              {blogPosts.map((post) => (
                <BlogCard key={post.slug} post={post} />
              ))}
            </div>

            <div className="text-center mb-12">
              <Link
                to="/blog"
                className="inline-flex items-center justify-center bg-white/10 backdrop-blur-md px-6 py-3 rounded-full text-yellow-400 font-semibold shadow-md hover:bg-white/20 transition"
              >
                View All Articles
              </Link>
            </div>
          </div>
        </section>
      )}
    </>
  );
}

export default HomePage;
